import {
  ALL_FALSE_POSITIVE,
  EReputationAnalysisCode,
  EReputationCategory,
  EReputationStatus,
} from "src/core/constants/eReputation";
import { EReputationInputType } from "src/core/gql/graphql";
import { EReputationMatchResult, MappedEntity } from "src/core/types/entity";
import { WatchlistIconType } from "src/core/types/watchlist";
import { getEntityWatchlistTypes } from "src/core/utils/entity";

/**
 * Construit les inputs pour la mise à jour de l'e-reputation.
 * Si la correspondance est confirmée, alors on met à jour les statuts en fonction des icônes d'alertes
 * de la correspondance.
 */
export const buildUpdateEntityEreputationInputs = ({
  entity,
  values,
  eReputationMatchResults,
}: {
  entity: MappedEntity;
  values: Record<string, boolean | undefined>;
  eReputationMatchResults: EReputationMatchResult[];
}): EReputationInputType[] => {
  const valuesArray = Object.entries(values).filter(([key]) => key !== ALL_FALSE_POSITIVE);
  const hasConfirmed = valuesArray.some(([, val]) => val === false);

  const defaultStatuses: { key: EReputationCategory; code: EReputationAnalysisCode }[] = [];
  if (![EReputationStatus.NO_ALERT, EReputationStatus.NO_ANALYSIS].includes(entity.pepStatus)) {
    defaultStatuses.push({
      key: EReputationCategory.PEP,
      code: EReputationAnalysisCode.FALSE_POSITIVE,
    });
  }
  if (
    ![EReputationStatus.NO_ALERT, EReputationStatus.NO_ANALYSIS].includes(entity.sanctionStatus)
  ) {
    defaultStatuses.push({
      key: EReputationCategory.SANCTION,
      code: EReputationAnalysisCode.FALSE_POSITIVE,
    });
  }

  if (hasConfirmed) {
    const confirmedEntityId = Object.entries(values).find(([, value]) => value === false)?.[0];
    const matchResult = eReputationMatchResults.find((match) => match.id === confirmedEntityId);

    if (matchResult) {
      const watchlistTypes: Set<WatchlistIconType> = getEntityWatchlistTypes(matchResult);

      const iconKeyMap: Record<string, EReputationCategory> = {
        [WatchlistIconType.PEP]: EReputationCategory.PEP,
        [WatchlistIconType.SAN_PERSON]: EReputationCategory.SANCTION,
        [WatchlistIconType.SAN_ENTITY]: EReputationCategory.SANCTION,
        [WatchlistIconType.SIE]: EReputationCategory.SANCTION,
      };

      watchlistTypes.forEach((type) => {
        const keyToUpdate = iconKeyMap[type];
        if (keyToUpdate) {
          const status = defaultStatuses.find((s) => s.key === keyToUpdate);
          if (status) {
            status.code =
              type === WatchlistIconType.PEP
                ? EReputationAnalysisCode.X_PEP
                : EReputationAnalysisCode.X_SANCTION;
          }
        }
      });
    }
  }

  const result = Object.values(defaultStatuses).map(({ key, code }) => ({
    categoryCode: key.toLowerCase(),
    analysisCode: hasConfirmed ? code : EReputationAnalysisCode.FALSE_POSITIVE,
  }));

  return result;
};
